import { MEDIA_TYPE } from '@constants/file';
import { formatVideoDuration } from '@helpers/upload-file';

// Mirrors TikTokConstants.PrivacyLevel in server/Integrations/Pluspoint.Integrations.TikTok — keep in step.
export const TIKTOK_PRIVACY = {
    PUBLIC: 'PUBLIC_TO_EVERYONE',
    FRIENDS: 'MUTUAL_FOLLOW_FRIENDS',
    FOLLOWERS: 'FOLLOWER_OF_CREATOR',
    SELF_ONLY: 'SELF_ONLY',
};

export const TIKTOK_PRIVACY_ORDER = [
    TIKTOK_PRIVACY.PUBLIC,
    TIKTOK_PRIVACY.FRIENDS,
    TIKTOK_PRIVACY.FOLLOWERS,
    TIKTOK_PRIVACY.SELF_ONLY,
];

// Where a server rule exists the value is the server's TikTokFailureCodes string; the rest are editor states.
export const TIKTOK_ISSUE = {
    NO_TIKTOK_LOCATION: 'noTikTokLocation',
    CREATOR_INFO_LOADING: 'creatorInfoLoading',
    CREATOR_INFO_FAILED: 'creatorInfoFailed',
    NO_CREATORS: 'noCreators',
    CANNOT_POST: 'tiktok_cannot_post_now',
    NO_COMMON_OPTION: 'noCommonOption',
    PRIVACY_REQUIRED: 'tiktok_settings_required',
    PRIVACY_UNAVAILABLE: 'tiktok_privacy_unavailable',
    DISCLOSURE_INCOMPLETE: 'tiktok_disclosure_incomplete',
    BRANDED_PRIVATE: 'tiktok_branded_private',
    VIDEO_TOO_LONG_FOR_CREATOR: 'video_too_long_for_creator',
};

export const CREATOR_INFO_STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
};

export const CANNOT_POST_REASON = {
    NOT_AUTHENTICATED: 'not_authenticated',
    NO_INTEGRATION: 'no_integration',
};

// Mirrors IsDefinitiveRejection on the server: these stay true until someone acts, so they block
// the save. A rate limit or an outage does not, and publish time enforces the rules again.
const DEFINITIVE_CANNOT_POST = [
    CANNOT_POST_REASON.NOT_AUTHENTICATED,
    CANNOT_POST_REASON.NO_INTEGRATION,
    'spam_risk',
    'active_user_cap',
];

const CANNOT_POST_KEY = {
    [CANNOT_POST_REASON.NOT_AUTHENTICATED]: 'SocialPosts.tiktok.reconnectAccount',
    [CANNOT_POST_REASON.NO_INTEGRATION]: 'SocialPosts.tiktok.chooseAccount',
};

export const defaultTikTokOptions = () => ({
    privacyLevel: '',
    allowComment: false,
    allowDuet: false,
    allowStitch: false,
    commercialContent: false,
    yourBrand: false,
    brandedContent: false,
});

export const normalizeTikTokOptions = saved => ({
    ...defaultTikTokOptions(),
    ...(saved || {}),
    privacyLevel: saved?.privacyLevel || '',
});

export const creatorHandle = (creator, fallback = '') =>
    creator?.username ? `@${creator.username}` : creator?.displayName || fallback;

export const hasVideo = (media = []) => media.some(m => m.type === MEDIA_TYPE.VIDEO);

export const isPhotoPost = (media = []) => media.length > 0 && !hasVideo(media);

export const withCommercialContent = (options, enabled) =>
    enabled
        ? { ...options, commercialContent: true }
        : { ...options, commercialContent: false, yourBrand: false, brandedContent: false };

// Turning branded content on while "Only me" is chosen clears the choice rather than silently
// switching the audience; the dropdown then asks for a new one.
export const withBrandedContent = (options, checked) => ({
    ...options,
    brandedContent: checked,
    ...(checked && options.privacyLevel === TIKTOK_PRIVACY.SELF_ONLY ? { privacyLevel: '' } : {}),
});

// Only an interaction no targeted account allows is cleared from the campaign. When some accounts
// allow it the choice is kept, and the publisher turns it off for the accounts that do not, so one
// restricted account never strips the capability from the rest.
export const applyCreatorRestrictions = (options, constraints) => ({
    ...options,
    allowComment: !!options.allowComment && !constraints.commentDisabledForAll,
    allowDuet: !!options.allowDuet && !constraints.duetDisabledForAll,
    allowStitch: !!options.allowStitch && !constraints.stitchDisabledForAll,
});

// A draft publishes nothing, so only the rules that judge what the user actually filled in apply
// to it. The server draws the same line.
const DRAFT_BLOCKING = [TIKTOK_ISSUE.DISCLOSURE_INCOMPLETE, TIKTOK_ISSUE.BRANDED_PRIVATE];

export const blocksDraft = issue => DRAFT_BLOCKING.includes(issue.code);

// The option list is authoritative only once a fetch has returned it. In view mode, while loading
// and after a failed fetch the stored level is shown as-is; blanking a required field reads as
// data loss.
export const resolvePrivacyChoice = (options, constraints, { readOnly = false, loading = false } = {}) => {
    const stored = options?.privacyLevel || '';
    if (readOnly || loading || !constraints.privacyOptions.length) {
        return { value: stored, levels: stored ? [stored] : [], selectable: false };
    }
    return {
        value: constraints.privacyOptions.includes(stored) ? stored : '',
        levels: constraints.privacyOptions,
        selectable: true,
    };
};

export const EMPTY_CONSTRAINTS = {
    privacyOptions: [],
    noCommonOption: false,
    conflictingHandle: '',
    commentDisabledBy: [],
    duetDisabledBy: [],
    stitchDisabledBy: [],
    commentDisabledForAll: false,
    duetDisabledForAll: false,
    stitchDisabledForAll: false,
    maxVideoDurationSec: null,
    maxVideoDurationBy: '',
    cannotPost: [],
};

// One campaign, many creators: the privacy list is the intersection, an interaction is off if any
// creator turned it off, and the video limit is the strictest creator's limit.
export const deriveTikTokConstraints = (creators = [], isAudited = true, fallbackHandle = '') => {
    if (!creators.length) return EMPTY_CONSTRAINTS;
    const handleOf = creator => creatorHandle(creator, fallbackHandle);
    const postable = creators.filter(c => c.canPost);
    const allowed = isAudited ? TIKTOK_PRIVACY_ORDER : [TIKTOK_PRIVACY.SELF_ONLY];
    const privacyOptions = postable.length
        ? allowed.filter(level => postable.every(c => (c.privacyLevelOptions || []).includes(level)))
        : [];
    const disabledBy = key => postable.filter(c => c[`${key}Disabled`]).map(handleOf);
    const disabledForAll = key => postable.length > 0 && postable.every(c => c[`${key}Disabled`]);
    // Naming an account only helps if removing it would leave the rest with something in common.
    const blocker = postable.find(candidate => {
        const others = postable.filter(c => c !== candidate);
        return (
            others.length > 0 && allowed.some(level => others.every(c => (c.privacyLevelOptions || []).includes(level)))
        );
    });
    const limited = postable
        .filter(c => c.maxVideoPostDurationSec > 0)
        .sort((a, b) => a.maxVideoPostDurationSec - b.maxVideoPostDurationSec)[0];
    return {
        privacyOptions,
        noCommonOption: postable.length > 0 && privacyOptions.length === 0,
        conflictingHandle: blocker ? handleOf(blocker) : '',
        commentDisabledBy: disabledBy('comment'),
        duetDisabledBy: disabledBy('duet'),
        stitchDisabledBy: disabledBy('stitch'),
        commentDisabledForAll: disabledForAll('comment'),
        duetDisabledForAll: disabledForAll('duet'),
        stitchDisabledForAll: disabledForAll('stitch'),
        maxVideoDurationSec: limited ? limited.maxVideoPostDurationSec : null,
        maxVideoDurationBy: limited ? handleOf(limited) : '',
        cannotPost: creators.filter(c => !c.canPost),
    };
};

const disclosureIncomplete = options => !!options?.commercialContent && !options.yourBrand && !options.brandedContent;

const brandedPrivate = options =>
    !!options?.commercialContent && !!options.brandedContent && options.privacyLevel === TIKTOK_PRIVACY.SELF_ONLY;

// Every issue surfaces on the tab; only a blocking one disables the schedule button. A creator
// TikTok simply could not be reached for is a warning, matching the server, which skips those
// rules rather than failing the save.
export const getTikTokIssues = ({
    options,
    constraints = EMPTY_CONSTRAINTS,
    media = [],
    status,
    creatorCount = 0,
    fallbackHandle = '',
}) => {
    const issues = [];
    const cannotCompleteForm = !options?.privacyLevel && constraints.privacyOptions.length === 0;
    if (status === CREATOR_INFO_STATUS.IDLE) {
        issues.push({
            code: TIKTOK_ISSUE.NO_TIKTOK_LOCATION,
            key: 'SocialPosts.tiktok.noTikTokLocation',
            blocking: true,
        });
    }
    if (status === CREATOR_INFO_STATUS.LOADING) {
        issues.push({
            code: TIKTOK_ISSUE.CREATOR_INFO_LOADING,
            key: 'SocialPosts.tiktok.loadingCreatorInfo',
            blocking: true,
        });
    }
    if (status === CREATOR_INFO_STATUS.ERROR) {
        // Without a stored audience there is nothing to save; with one, the post is complete and
        // the server re-checks it at publish.
        issues.push({
            code: TIKTOK_ISSUE.CREATOR_INFO_FAILED,
            key: 'SocialPosts.tiktok.creatorInfoFailed',
            blocking: !options?.privacyLevel,
        });
    }
    if (status === CREATOR_INFO_STATUS.READY && creatorCount === 0) {
        issues.push({ code: TIKTOK_ISSUE.NO_CREATORS, key: 'SocialPosts.tiktok.noCreators', blocking: true });
    }
    constraints.cannotPost.forEach(creator =>
        issues.push({
            code: TIKTOK_ISSUE.CANNOT_POST,
            key: CANNOT_POST_KEY[creator.cannotPostReason] || 'SocialPosts.tiktok.cannotPostNow',
            options: { handle: creatorHandle(creator, fallbackHandle) },
            // A transient failure is only a warning while the user can still finish the form. With
            // no audience stored and none on offer there is nothing left to save, so it blocks.
            blocking: DEFINITIVE_CANNOT_POST.includes(creator.cannotPostReason) || cannotCompleteForm,
        }),
    );
    if (constraints.noCommonOption) {
        issues.push({
            code: TIKTOK_ISSUE.NO_COMMON_OPTION,
            key: constraints.conflictingHandle
                ? 'SocialPosts.tiktok.privacyNoCommonOption'
                : 'SocialPosts.tiktok.privacyNoCommonOptionAny',
            options: { handle: constraints.conflictingHandle },
            blocking: true,
        });
    }
    if (!options?.privacyLevel) {
        if (constraints.privacyOptions.length) {
            issues.push({
                code: TIKTOK_ISSUE.PRIVACY_REQUIRED,
                key: 'SocialPosts.tiktok.privacyChooseAgain',
                blocking: true,
            });
        }
    } else if (constraints.privacyOptions.length && !constraints.privacyOptions.includes(options.privacyLevel)) {
        issues.push({
            code: TIKTOK_ISSUE.PRIVACY_UNAVAILABLE,
            key: 'SocialPosts.tiktok.privacyChooseAgain',
            blocking: true,
        });
    }
    if (disclosureIncomplete(options)) {
        issues.push({
            code: TIKTOK_ISSUE.DISCLOSURE_INCOMPLETE,
            key: 'SocialPosts.tiktok.commercialIncomplete',
            blocking: true,
        });
    }
    if (brandedPrivate(options)) {
        issues.push({
            code: TIKTOK_ISSUE.BRANDED_PRIVATE,
            key: 'SocialPosts.tiktok.brandedNotPrivate',
            blocking: true,
        });
    }
    const limit = constraints.maxVideoDurationSec;
    if (limit && media.some(m => m.type === MEDIA_TYPE.VIDEO && m.duration != null && m.duration > limit)) {
        issues.push({
            code: TIKTOK_ISSUE.VIDEO_TOO_LONG_FOR_CREATOR,
            key: 'SocialPosts.videoTooLongForCreator',
            options: { handle: constraints.maxVideoDurationBy, limit: formatVideoDuration(limit) },
            blocking: true,
        });
    }
    return issues;
};
